<script setup>
// Config do nó HTTP Request — método, URL, headers (lista chave-valor), body, timeout, salvar resposta.
// Espelha HttpRequestConfig do original.
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';

const props = defineProps({ node: { type: Object, required: true } });

const { t } = useI18n();

const d = computed(() => props.node.data);

const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => ({
  value: m,
  label: m,
}));
const hasBody = computed(() =>
  ['POST', 'PUT', 'PATCH'].includes(d.value.method)
);
const headers = computed(() => d.value.headers || {});
const newHeaderKey = ref('');

const showHeaders = ref(false);
const addHeader = () => {
  const k = newHeaderKey.value.trim();
  if (!k) return;
  if (!d.value.headers) d.value.headers = {};
  d.value.headers[k] = '';
  newHeaderKey.value = '';
};
const removeHeader = k => {
  delete d.value.headers[k];
};
const setHeader = (k, v) => {
  d.value.headers[k] = v;
};

onMounted(() => {
  if (!d.value.method) d.value.method = 'POST';
  if (d.value.timeout === undefined) d.value.timeout = 10000;
  if (!d.value.headers) d.value.headers = {};
});
</script>

<template>
  <div>
    <FlowField
      :model-value="d.method"
      :label="t('CHATBOT.nodeConfig.httpRequest.methodLabel')"
      type="select"
      :options="METHODS"
      @update:model-value="d.method = $event"
    />
    <FlowField
      :model-value="d.url"
      :label="t('CHATBOT.nodeConfig.httpRequest.urlLabel')"
      type="text"
      placeholder="https://exemplo.com/webhook"
      :hint="t('CHATBOT.nodeConfig.httpRequest.urlHint')"
      @update:model-value="d.url = $event"
    />

    <div class="mb-3">
      <button
        class="text-[0.6875rem] text-n-blue-11 hover:underline"
        @click="showHeaders = !showHeaders"
      >
        {{
          showHeaders
            ? t('CHATBOT.nodeConfig.httpRequest.hide')
            : t('CHATBOT.nodeConfig.httpRequest.show')
        }}
        {{
          t('CHATBOT.nodeConfig.httpRequest.headersCount', {
            count: Object.keys(headers).length,
          })
        }}
      </button>
      <div v-if="showHeaders" class="mt-1 space-y-1">
        <div v-for="(v, k) in headers" :key="k" class="flex items-center gap-1">
          <input
            :value="k"
            disabled
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 flex-1 !bg-n-alpha-1"
          />
          <input
            :value="v"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 flex-1"
            :placeholder="
              t('CHATBOT.nodeConfig.httpRequest.headerValuePlaceholder')
            "
            @input="setHeader(k, $event.target.value)"
          />
          <button class="text-xs text-n-ruby-11" @click="removeHeader(k)">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-3.5 h-3.5"
            >
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="flex gap-1">
          <input
            v-model="newHeaderKey"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 flex-1"
            :placeholder="
              t('CHATBOT.nodeConfig.httpRequest.headerNamePlaceholder')
            "
            @keyup.enter="addHeader"
          />
          <button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1 whitespace-nowrap"
            @click="addHeader"
          >
            {{ t('CHATBOT.nodeConfig.httpRequest.addHeader') }}
          </button>
        </div>
      </div>
    </div>

    <FlowField
      v-if="hasBody"
      :model-value="d.body"
      :label="t('CHATBOT.nodeConfig.httpRequest.bodyLabel')"
      type="textarea"
      :rows="6"
      placeholder='{ "campo": "{{variavel}}" }'
      picker
      @update:model-value="d.body = $event"
    />
    <FlowField
      :model-value="d.timeout"
      :label="t('CHATBOT.nodeConfig.httpRequest.timeoutMsLabel')"
      type="number"
      :min="1000"
      :max="60000"
      :step="1000"
      @update:model-value="d.timeout = $event"
    />
    <FlowField
      :model-value="d.saveResponseTo"
      :label="t('CHATBOT.nodeConfig.httpRequest.saveResponseLabel')"
      type="text"
      placeholder="nomeVariavel"
      @update:model-value="d.saveResponseTo = $event"
    />
  </div>
</template>
